import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { Container, Grid, Typography, Box } from '@mui/material';
import getStyles from './styles';

const OurProjectsBanner = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });

  // Parallax transformations
  const yBackground = useTransform(scrollYProgress, [0, 1], [0, 400]);
  const yImage = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const yContent = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const opacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.2]);

  const stats = [
    { value: '20+', label: 'Clients' },
    { value: '98%', label: 'Satisfaction' },
    { value: '50+', label: 'Projects' }
  ];

  const styles = getStyles();

  return (
    <Box component="section" ref={containerRef} sx={styles.bannerSection}>
      <motion.div style={{ y: yBackground }}>
        <Box sx={styles.animatedBackground} />
      </motion.div>
      <motion.div style={{ y: yBackground, scale }}>
        <Box sx={styles.floatingShape1} />
      </motion.div>
      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, -200]) }}
      >
        <Box sx={styles.floatingShape2} />
      </motion.div>
      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, 150]) }}
      >
        <Box sx={styles.particle1} />
      </motion.div>
      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, -150]) }}
      >
        <Box sx={styles.particle2} />
      </motion.div>
      <motion.div
        style={{ y: useTransform(scrollYProgress, [0, 1], [0, 100]) }}
      >
        <Box sx={styles.particle3} />
      </motion.div>
      <Box sx={styles.gradientOverlay} />
      <Container maxWidth="xl" sx={styles.contentContainer}>
        <Grid container spacing={4} sx={styles.gridContainer}>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <motion.div
              style={{ y: yContent, opacity }}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <Box sx={styles.contentSection}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  <Box sx={styles.premiumBadge}>
                    <span>✨</span>
                    <Typography variant="body2" sx={styles.badgeText}>
                      Premium Digital Solutions
                    </Typography>
                  </Box>
                </motion.div>
                {/* <Typography variant="h1" sx={styles.mainTitle}>
                  Our Works
                </Typography> */}
                <Box sx={styles.description}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                  >
                    <Typography sx={styles.descriptionText}>
                      We&apos;ve partnered with{' '}
                      <span>government bodies, enterprises, and startups</span>{' '}
                      to craft powerful digital experiences — from portals and
                      platforms to enterprise-grade web solutions.
                    </Typography>
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                  >
                    <Typography sx={styles.descriptionText}>
                      Every project we deliver is built with one goal: to help
                      our clients{' '}
                      <span>grow smarter, faster, and stronger</span> in the
                      digital world.
                    </Typography>
                  </motion.div>
                </Box>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 0.5 }}
                >
                  <Box sx={styles.statsGrid}>
                    {stats.map((stat, index) => (
                      <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                        whileHover={{ scale: 1.05, y: -5 }}
                      >
                        <Box
                          sx={styles.statCard}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              'translateY(-5px)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0px)';
                          }}
                        >
                          <Box sx={styles.statGlow} />
                          <Box sx={styles.statContent}>
                            <Typography sx={styles.statValue}>
                              {stat.value}
                            </Typography>
                            <Typography sx={styles.statLabel}>
                              {stat.label}
                            </Typography>
                          </Box>
                        </Box>
                      </motion.div>
                    ))}
                  </Box>
                </motion.div>
              </Box>
            </motion.div>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <motion.div
              style={{ y: yImage, opacity }}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, ease: 'easeOut' }}
            >
              <Box sx={styles.imageContainer}>
                <Box sx={styles.imageGlow} />
                <Box sx={styles.mainImage}>
                  <Box
                    component="img"
                    src="/images/hero-workspace.jpg"
                    alt="Modern digital workspace"
                    sx={{
                      width: '100%',
                      height: 'auto',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                  <Box sx={styles.imageOverlay} />
                </Box>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                >
                  <Box sx={styles.floatingBadge}>
                    <Typography variant="h4" sx={styles.successRate}>
                      100%
                    </Typography>
                    <Typography variant="caption" sx={styles.successLabel}>
                      Success Rate
                    </Typography>
                  </Box>
                </motion.div>
              </Box>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
      <Box sx={styles.bottomFade} />
    </Box>
  );
};

export default OurProjectsBanner;
